import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { LinkTo } from '@ember/routing';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { isAlive } from 'webapp/utils/common';

import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';

import type Source from 'webapp/models-custom/source';
import type SourceRecord from 'webapp/models/source';

export default class BoardPopupMigrationToolComponent extends PopupInnerModalComponent<PopupData.Board.Resolve> {
	@service('connect') declare connectService: Service.Connect;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;

	@tracked isAllDone: boolean;
	@tracked resolvableSources: SourceRecord[];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.isAllDone = false;
		this.resolvableSources = this.board.sourcesResolvable;
	}

	get board() {
		return this.popupData.model;
	}

	get sourcesToResolve() {
		return this.mmSourcesService.createModelsFromRecordsArray(this.resolvableSources);
	}

	checkIfAllDone() {
		next(this, () => {
			if (this.sourcesToResolve.length === 0) {
				this.isAllDone = true;
				this.scheduleReposition();
			}
		});
	}

	onResolutionEnd(source: Source, isSuccess: boolean) {
		if (isAlive(this)) {
			if (isSuccess) {
				this.resolvableSources = this.resolvableSources.filter(
					(resolvableSource) => resolvableSource.id !== source.id,
				);
				this.checkIfAllDone();
			}
		}
	}

	reconnectSource(source: Source, reauthorize = false) {
		void this.connectService
			.connectSourceFromIdOrKey(source.sourceType.key, {
				sourceId: +source.id!,
				reauthorize,
				origin: 'modalResolveboard',
			})
			.then(({ isSuccess }) => {
				this.onResolutionEnd(source, isSuccess);
			});
	}

	@action
	resolve(source: Source) {
		if (source.canReconnect || source.canReauthorize) {
			this.reconnectSource(source, source.canReauthorize);
		}
	}
	<template>
		{{#if this.isAllDone}}
			<div class="allDone">
				<span class="circleCheck"></span>
				<p> You've resolved all connection issues for this dashboard.</p>
				<a role="button" class="button theme-grey-outlined" {{on "click" this.close}}>
					Done
				</a>
			</div>
		{{else}}
			<div class="title red-on-transparent">
				<CommonUiIconComponent @name="AlertFill" @size="lg" />
				Resolve connection issues for "{{this.board.name}}"
			</div>
			<div class="content">
				<ul class="sourceList">
					{{#each this.sourcesToResolve as |source|}}
						<li>
							<AvatarOrInitialsComponent
								@name={{source.title}}
								@avatar={{source.realAvatar}}
							/>
							<strong>{{source.subtitle}}</strong>
							<small>{{source.title}}</small>
							<div class="status">
								<span class="status-icon">
									<CommonUiIconComponent @name="AlertFill" @size="sm" />
									{{source.statusTitle}}
								</span>
								{{#if source.statusDescription}}
									<p>{{{source.statusDescriptionWithLinks}}}</p>
								{{/if}}
							</div>
							{{#if source.canResolveActionableStatus}}
								<a
									role="button"
									class="button"
									{{on "click" (fn this.resolve source)}}
								>
									{{source.statusActionCapitalized}}
								</a>
							{{else}}
								<LinkTo class="button" @route="datamanager">View in Data manager</LinkTo>
							{{/if}}
						</li>
					{{/each}}
				</ul>
			</div>
		{{/if}}
	</template>
}
